import React, {useState, useRef} from 'react'
import PageLayout from '../../components/layout/PageLayout'
import useQueryPromotions from '../../hooks/api/promotion/useQueryPromotions'
import WizButton from '../../components/button/WizButton'
import WizSelectButton from '../../components/button/WizSelectButton'
import Text from '../../components/text/Text'
import {IPromotions} from '../../apis/promotion/list'
import {PromotionList} from './components/PromotionList'
import {PromotionDetailsForm} from './components/PromotionDetailsForm'
import {PromotionSlotList} from './components/PromotionSlotList'
import {PromotionSlotForm} from './components/PromotionSlotForm'
import useQueryPromotionDetail from '../../hooks/api/promotion/useQueryPromotionDetail'
import {IPromotionItem, deletePromotionItem, SlotType} from '../../apis/promotion/item'
import WizModal from '../../components/modal/WizModal'
import {Collapse} from 'antd'
import {CaretRightOutlined, CloseOutlined} from '@ant-design/icons'

const {Panel} = Collapse

const SLOT_OPTIONS: {id: SlotType; value: string}[] = [
    {id: 'leaderboard-banner', value: 'Leaderboard Top'},
    {id: 'tradermap-card', value: 'Trader Map Top'},
    {id: 'landing-event', value: 'Landing Banner'},
    {id: 'popup-event', value: 'Landing Popup'},
    {id: 'announce-event', value: 'Announce Event'},
    {id: 'bottom-sheet', value: 'BottomSheet'},
    {id: 'account-banner', value: 'Account Banner'},
    {id: 'leaderboard-header', value: 'Leaderboard Header'},
]

const DEVICE_OPTIONS = [
    {id: 'pc', value: 'PC'},
    {id: 'mo', value: 'Mobile'},
]

interface IProps {
    className?: string
}

export function Promotion({className}: IProps) {
    const [active, setActive] = React.useState<boolean>(true)
    const {
        data: promotionList,
        refetch: refetchList,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useQueryPromotions(active)

    const [selectedPromotion, setSelectedPromotion] = useState<IPromotions | null>(null)
    const [viewMode, setViewMode] = useState<'create' | 'edit' | 'idle'>('idle') // Added 'idle' for full-width list state

    // Slot Management
    const {data: slotItems, refetch: refetchSlots} = useQueryPromotionDetail(String(selectedPromotion?.id || ''))
    const [isCreatingSlot, setIsCreatingSlot] = useState(false)
    const [editingSlotItem, setEditingSlotItem] = useState<IPromotionItem | undefined>(undefined)

    // Modal Ref
    const slotModalRef = useRef<any>()

    // Slot Matrix UI State
    const [isSlotMatrixOpen, setIsSlotMatrixOpen] = useState(false)

    const activeList = [
        {id: 'active', value: 'Active'},
        {id: 'inactive', value: 'All'},
    ]

    const handleCreatePromotion = () => {
        setSelectedPromotion(null)
        setViewMode('create')
        setIsCreatingSlot(false)
        setEditingSlotItem(undefined)
    }

    const handleSelectPromotion = (id: string | null) => {
        if (id) {
            const promotion = promotionList?.find(p => p.id === id) || null
            setSelectedPromotion(promotion)
            setViewMode('edit')
        } else {
            // If checking same ID or null, user might expect toggle, but let's stick to explicit select
            // To close, they can use the close button or deselect logic if we want toggle behavior
            setSelectedPromotion(null)
            setViewMode('idle')
        }
        setIsCreatingSlot(false)
        setEditingSlotItem(undefined)
    }

    const handleCloseDetails = () => {
        setSelectedPromotion(null)
        setViewMode('idle')
    }

    const handlePromotionSaved = (newData?: IPromotions) => {
        refetchList()
        if (newData) {
            setSelectedPromotion(newData)
            setViewMode('edit')
        } else {
            // After save (create), maybe go to edit mode of that item or back to list?
            // Usually stay in edit mode
            if (viewMode === 'create') {
                // If we don't have the new data, return to list for safety?
                // Or just keep create form open?
                // Given previous logic, let's keep it consistent
                setViewMode('idle') // Go back to list to see the new item
                setSelectedPromotion(null)
            }
        }
    }

    const handleDeleteSlot = async (item: IPromotionItem) => {
        if (window.confirm('Are you sure you want to delete this slot?')) {
            const res = await deletePromotionItem(String(selectedPromotion?.id), Number(item.id))
            if (res?.data?.success) {
                refetchSlots()
                slotModalRef.current?.close()
                setIsCreatingSlot(false)
                setEditingSlotItem(undefined)
            } else {
                alert('Failed to delete slot')
            }
        }
    }

    const openSlotModal = (item?: IPromotionItem) => {
        setEditingSlotItem(item)
        setIsCreatingSlot(!item)
        slotModalRef.current?.show()
    }

    const isDetailVisible = viewMode !== 'idle'

    return (
        <PageLayout title="Promotions" className={className}>
            <div className="flex h-full gap-6 sm:flex-col overflow-hidden relative">
                {/* Left Sidebar: Promotion List */}
                <div
                    className={`flex-shrink-0 flex flex-col bg-white border border-line02 rounded-lg overflow-hidden h-[calc(100vh-140px)] sm:h-[40vh] transition-all duration-500 ease-in-out ${
                        isDetailVisible ? 'w-[230px]' : 'w-1/3'
                    } sm:w-full`}>
                    <div className="p-4 border-b border-line02 gap-x-3 flex items-center justify-between sticky top-0 bg-white z-10">
                        <Text className="text-h3 font-bold">List</Text>
                        <WizSelectButton
                            data={activeList}
                            activeValue={activeList?.find(l => l?.id === (active ? 'active' : 'inactive'))}
                            onChange={e => {
                                setActive(e.id === 'active' ? true : false)
                                setSelectedPromotion(null)
                                setViewMode('idle')
                            }}
                        />
                    </div>

                    <div className="p-2 space-y-2 overflow-y-auto flex-1 custom-scrollbar">
                        <WizButton
                            text={'Create New Promotion'}
                            type="primary"
                            onClick={handleCreatePromotion}
                            className="w-full mb-2"
                        />
                        <PromotionList
                            promotions={promotionList || []}
                            activePromotionId={selectedPromotion?.id}
                            onSelect={handleSelectPromotion}
                            isCompact={isDetailVisible}
                            hasNextPage={hasNextPage}
                            isFetchingNextPage={isFetchingNextPage}
                            onLoadMore={() => fetchNextPage()}
                        />
                    </div>
                </div>

                {/* Right Main Content */}
                <div
                    className={`flex-1 overflow-y-auto sm:overflow-y-visible h-[calc(100vh-140px)] pr-2 transition-all duration-500 ease-in-out`}>
                    {/* Close Button implementation for better UX */}
                    {isDetailVisible && (
                        <div className="flex justify-end mb-2">
                            <button
                                onClick={handleCloseDetails}
                                className="text-gray-400 hover:text-gray-600 flex items-center gap-1 text-sm bg-white px-3 py-1 rounded-full shadow-sm border border-line02">
                                <CloseOutlined rev={undefined} /> Close Details
                            </button>
                        </div>
                    )}

                    {viewMode === 'create' ? (
                        <div className="p-6 bg-white border border-line02 rounded-lg">
                            <Text className="text-h2 font-bold mb-6">Create New Promotion</Text>
                            <PromotionDetailsForm viewMode={'create'} onSuccess={handlePromotionSaved} />
                        </div>
                    ) : selectedPromotion ? (
                        <div className="space-y-6 animate-fadeIn">
                            {/* 1. Promotion Details (Collapsible Accordion) */}
                            <div className="bg-white border border-line02 rounded-lg overflow-hidden shadow-sm">
                                <Collapse
                                    bordered={false}
                                    defaultActiveKey={['1']} // Open by default, let user close interaction
                                    expandIcon={({isActive}) => (
                                        <CaretRightOutlined rotate={isActive ? 90 : 0} rev={undefined} />
                                    )}
                                    className="bg-white">
                                    <Panel
                                        header={
                                            <div className="flex justify-between items-center w-full pr-4">
                                                <div className="flex items-center gap-2">
                                                    <Text className="text-h3 font-bold">{selectedPromotion.name}</Text>
                                                    {(() => {
                                                        const now = new Date()
                                                        const start = new Date(selectedPromotion.start_at)
                                                        const end = new Date(selectedPromotion.end_at)
                                                        let badgeClass = 'bg-gray-100 text-gray-600'
                                                        let label = selectedPromotion.status?.toUpperCase()

                                                        if (selectedPromotion.status === 'active') {
                                                            if (now < start) {
                                                                badgeClass = 'bg-orange-100 text-orange-700'
                                                                label = 'SCHEDULED'
                                                            } else if (now > end) {
                                                                badgeClass = 'bg-gray-100 text-gray-600'
                                                                label = 'ENDED'
                                                            } else {
                                                                badgeClass = 'bg-green-100 text-green-700'
                                                                label = 'ACTIVE'
                                                            }
                                                        }

                                                        return (
                                                            <span
                                                                className={`px-2 py-0.5 rounded text-ti3 font-bold ${badgeClass}`}>
                                                                {label}
                                                            </span>
                                                        )
                                                    })()}
                                                </div>
                                                <Text className="text-ti2 text-font03">
                                                    ID: {selectedPromotion.id} • Order: {selectedPromotion.priority}
                                                </Text>
                                            </div>
                                        }
                                        key="1">
                                        <div className="pt-2 border-t border-line01">
                                            <PromotionDetailsForm
                                                viewMode={'edit'}
                                                promotionData={selectedPromotion}
                                                onSuccess={handlePromotionSaved}
                                                onDelete={() => {
                                                    refetchList()
                                                    setSelectedPromotion(null)
                                                    setViewMode('idle')
                                                }}
                                            />
                                        </div>
                                    </Panel>
                                </Collapse>
                            </div>

                            {/* 2. Masonry Slots */}
                            <div className="p-6 bg-white border border-line02 rounded-lg shadow-sm min-h-[500px]">
                                <div className="flex justify-between items-center mb-6">
                                    <div className="flex items-center gap-2">
                                        <Text className="text-h3 font-bold">Promotion Slots</Text>
                                        <div
                                            onClick={() => setIsSlotMatrixOpen(!isSlotMatrixOpen)}
                                            className={`
                                            cursor-pointer px-2 py-0.5 rounded-full text-ti3 font-bold transition-all select-none
                                            ${
                                                isSlotMatrixOpen
                                                    ? 'bg-primary text-white ring-2 ring-primary/20'
                                                    : 'bg-primary/10 text-primary hover:bg-primary/20'
                                            }
                                        `}>
                                            {slotItems?.length || 0} Items
                                        </div>
                                    </div>
                                    <WizButton
                                        text="+ Add Slot"
                                        type="primary"
                                        onClick={() => openSlotModal(undefined)}
                                    />
                                </div>

                                {/* Slot Status Matrix Accordion */}
                                {isSlotMatrixOpen && (
                                    <div className="mb-6 mx-1 p-4 bg-gray-50/80 rounded-lg border border-line02 animate-fadeIn">
                                        <div className="flex flex-wrap gap-y-3">
                                            {DEVICE_OPTIONS.map(device => (
                                                <div key={device.id}>
                                                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-line02/50">
                                                        <span className="text-lg">
                                                            {device.id === 'pc' ? '💻' : '📱'}
                                                        </span>
                                                        <Text className="text-ti2 font-bold text-font01">
                                                            {device.value}
                                                        </Text>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {SLOT_OPTIONS.map(slot => {
                                                            const exists = slotItems?.some(
                                                                item =>
                                                                    item.slot === slot.id && item.device === device.id,
                                                            )
                                                            return (
                                                                <div
                                                                    key={`${device.id}-${slot.id}`}
                                                                    className={`
                                                                    px-2.5 py-1.5 rounded-[4px] text-[11px] font-bold border transition-all
                                                                    ${
                                                                        exists
                                                                            ? 'bg-green-100 text-green-700 border-green-200 shadow-sm'
                                                                            : 'bg-white text-gray-300 border-line02 decoration-gray-300'
                                                                    }
                                                                `}>
                                                                    {slot.value}
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <PromotionSlotList
                                    items={slotItems || []}
                                    onEdit={item => openSlotModal(item)}
                                    promotionId={String(selectedPromotion.id)}
                                    onRefresh={refetchSlots}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-gray-50/50 rounded-lg border border-dashed border-line02">
                            <Text className="text-h3 sm:hidden">Select a promotion from the left list</Text>
                            <Text className="text-h3 hidden sm:block py-10">Select a promotion from the top list</Text>
                        </div>
                    )}
                </div>
            </div>

            {/* Slot Modal */}
            <WizModal
                ref={slotModalRef}
                title={isCreatingSlot ? 'Add New Slot' : 'Edit Slot'}
                className="!w-[800px] !max-w-none">
                <div className="py-4">
                    <PromotionSlotForm
                        promotionId={String(selectedPromotion?.id || '')}
                        item={editingSlotItem}
                        onSuccess={() => {
                            refetchSlots()
                            slotModalRef.current?.close()
                        }}
                        onCancel={() => {
                            slotModalRef.current?.close()
                        }}
                        onDelete={editingSlotItem ? () => handleDeleteSlot(editingSlotItem) : undefined}
                    />
                </div>
            </WizModal>
        </PageLayout>
    )
}
